import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiDownload, FiTrash2, FiUpload, FiFile } from 'react-icons/fi';
import './DocumentManager.css';

const API_URL = '/api';

export default function DocumentManager({ taskId, phaseId }) {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [description, setDescription] = useState('');
  const [docName, setDocName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, [taskId, phaseId]);

  const loadDocuments = async () => {
    try {
      if (taskId) {
        const response = await axios.get(`${API_URL}/documents/task/${taskId}`);
        setDocuments(response.data);
        return;
      }
      if (phaseId) {
        const response = await axios.get(`${API_URL}/documents/phase/${phaseId}`);
        setDocuments(response.data);
        return;
      }
      const response = await axios.get(`${API_URL}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Sélectionnez un fichier');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    if (taskId) formData.append('taskId', taskId);
    if (phaseId) formData.append('phaseId', phaseId);
    if (docName) formData.append('name', docName);
    formData.append('description', description);

    try {
      await axios.post(`${API_URL}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSelectedFile(null);
      setDescription('');
      setShowUploadForm(false);
      await loadDocuments();
    } catch (error) {
      console.error('Erreur upload:', error);
      alert("Erreur lors de l'upload du fichier");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (documentId, fileName) => {
    window.open(`${API_URL}/documents/download/${documentId}`, '_blank');
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/documents/${documentId}`);
      await loadDocuments();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    return ext;
  };

  return (
    <div className="document-manager">
      <div className="doc-header">
        <h4>📎 Documents</h4>
        {!showUploadForm && (
          <button 
            className="btn-upload-toggle"
            onClick={() => setShowUploadForm(true)}
          >
            <FiUpload /> Ajouter document
          </button>
        )}
      </div>

      {showUploadForm && (
        <div className="upload-form-container">
          <form onSubmit={handleUpload} className="upload-form">
            <div className="form-group">
              <label>Fichier *</label>
              <input
                type="file"
                onChange={handleFileSelect}
                required
              />
            </div>
            <div className="form-group">
              <label>Nom (optionnel)</label>
              <input
                type="text"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder="Nom visible pour ce document"
              />
            </div>
            <div className="form-group">
              <label>Description (optionnel)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Facture, Plan, Permis..."
              />
            </div>
            <div className="upload-actions">
              <button 
                type="submit" 
                className="btn-submit"
                disabled={uploading}
              >
                {uploading ? 'Upload...' : 'Télécharger'}
              </button>
              <button 
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowUploadForm(false);
                  setSelectedFile(null);
                  setDescription('');
                }}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="documents-list">
        {documents.length === 0 ? (
          <p className="empty-message">Aucun document</p>
        ) : (
          documents.map(doc => (
            <div key={doc.id} className="document-item">
              <div className="doc-info">
                <div className="doc-icon">
                  <FiFile />
                </div>
                <div className="doc-details">
                  <div className="doc-name">{doc.name || doc.original_name}</div>
                  {doc.description && (
                    <div className="doc-description">{doc.description}</div>
                  )}
                  <div className="doc-meta">
                    {(doc.file_size / 1024).toFixed(1)} KB • {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>
              <div className="doc-actions">
                <button
                  className="btn-doc-action"
                  onClick={() => handleDownload(doc.id, doc.original_name)}
                  title="Télécharger"
                >
                  <FiDownload />
                </button>
                <button
                  className="btn-doc-action delete"
                  onClick={() => handleDelete(doc.id)}
                  title="Supprimer"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
